/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * PrecinctView — single-precinct deep dive.
 *
 * Fully wired to the backend as of 2026-04-10. On mount we fetch
 *   GET /voters/by-precinct/{precinctNum}
 * which returns core metrics + tier / party / city / age breakdowns +
 * real past-election turnout history joined from voter_elections.
 *
 * Static dashboardData.ts is still used for the surrounding
 * navigation chrome (breadcrumb, subregion lookup, map file paths),
 * but the actual numbers on this page now come from Postgres.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import { ROUTES, GMAPS, LD_COLORS } from '@/data/dashboardData';
import { useMapRegions } from '@/context/MapRegionsContext';
import { regionForPrecinct } from '@/api/geoRegions';
import { LD_NAMES } from '@/lib/computeStats';
import { Breadcrumb } from '@/components/shared';
import client from '@/api/client';
import { InfoTip } from '@/components/InfoTip';
import { downloadWalkList, triggerBrowserDownload, openHtmlInNewTab } from '@/api/exports';
import { TierLegend } from '@/components/TierLegend';
import { VisualKey } from '@/components/VisualKey';
import { TierPill } from '@/components/TierPill';
import { DeltaChip } from '@/components/DeltaChip';
import { BarMeter } from '@/components/BarMeter';
import { useCountyBaselines } from '@/lib/baselines';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { OnboardingModal, OnboardingHelpButton } from '@/components/OnboardingModal';
import { useAuth } from '@/context/AuthContext';
import { ViewHeader } from '@/components/ViewHeader';

// -------------------------------------------------------------------------
// Types mirroring backend/app/schemas/voter.py → PrecinctDetailResponse
// -------------------------------------------------------------------------

type AgeBucketCount = { bucket: string; count: number };
type ElectionHistoryPoint = {
  election_date: string;
  election_type: string;
  voter_count: number;
};
type PrecinctDetail = {
  precinct_num: string;
  precinct_name: string;
  total_voters: number;
  active_voters: number;
  avg_score: number;
  super_voter_pct: number;
  primary_voter_pct: number;
  by_tier: Record<string, number>;
  by_party: Record<string, number>;
  by_city: Record<string, number>;
  by_age_bucket: AgeBucketCount[];
  election_history: ElectionHistoryPoint[];
};

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

const TIER_DISPLAY_ORDER = ['SUPER_VOTER', 'HOT', 'WARM', 'COOL'];
const TIER_LABELS: Record<string, string> = {
  SUPER_VOTER: 'Super Voter',
  HOT: 'Hot',
  WARM: 'Warm',
  COOL: 'Cool',
};

const PARTY_LABELS: Record<string, string> = {
  R: 'Republican',
  D: 'Democratic',
  I: 'Independent',
  L: 'Libertarian',
  G: 'Green',
  C: 'Constitution',
  UNKNOWN: 'No Party',
};

function toChartData(
  dict: Record<string, number>,
  order?: string[],
  labels?: Record<string, string>,
) {
  const keys = order ?? Object.keys(dict);
  return keys
    .filter((k) => (dict[k] ?? 0) > 0 || order !== undefined)
    .map((k) => ({
      name: labels?.[k] ?? k,
      value: dict[k] ?? 0,
    }));
}

// -------------------------------------------------------------------------
// Stat tile (replaces BenchStat now that bench values aren't in the API)
// -------------------------------------------------------------------------

function Stat({
  label,
  value,
  suffix = '',
  termId,
  children,
}: {
  label: string;
  value: string | number;
  suffix?: string;
  termId?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-bg-raised border border-border-base px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted flex items-center">
        <span>{label}</span>
        {termId && <InfoTip termId={termId} compact />}
      </div>
      <div className="mt-0.5 text-xl font-bold text-fg-primary tabular-nums">
        {value}
        {suffix}
      </div>
      {children && <div className="mt-1 space-y-1">{children}</div>}
    </div>
  );
}

// -------------------------------------------------------------------------
// Main view
// -------------------------------------------------------------------------

export const PrecinctView: React.FC<any> = ({ precinctNum, onDrill, onBack }) => {
  const { catalog: regions } = useMapRegions();
  const [data, setData] = useState<PrecinctDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | undefined>(undefined);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [downloadingHtml, setDownloadingHtml] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const { baselines: countyBl } = useCountyBaselines();
  const reduceMotion = useReducedMotion();
  const { user } = useAuth();

  // PII_READ_ROLES: admin, manager (coordinator), canvasser
  const piiReadRoles = new Set(['admin', 'manager', 'canvasser']);
  const canExportWalkList = user && piiReadRoles.has(user.role);

  const handleDownloadWalkListCsv = async () => {
    setDownloadingCsv(true);
    setDownloadError(null);
    try {
      const blob = await downloadWalkList(precinctNum, {
        tier: selectedTier,
        format: 'csv',
      });
      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const tierSuffix = selectedTier ? `-${selectedTier}` : '-all';
      const filename = `walk-list-p${precinctNum}${tierSuffix}-${dateStr}.csv`;
      triggerBrowserDownload(blob, filename);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setDownloadError('Access denied. You do not have permission to export walk lists.');
      } else if (status === 404) {
        setDownloadError(`No voters found for precinct ${precinctNum}.`);
      } else {
        setDownloadError(status ? `Error ${status}` : 'Failed to download walk list.');
      }
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handlePrintWalkList = async () => {
    setDownloadingHtml(true);
    setDownloadError(null);
    try {
      const blob = await downloadWalkList(precinctNum, {
        tier: selectedTier,
        format: 'html',
      });
      openHtmlInNewTab(blob);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setDownloadError('Access denied.');
      } else if (status === 404) {
        setDownloadError(`No voters found for precinct ${precinctNum}.`);
      } else {
        setDownloadError(status ? `Error ${status}` : 'Failed to open print preview.');
      }
    } finally {
      setDownloadingHtml(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    client
      .get<PrecinctDetail>(`/voters/by-precinct/${encodeURIComponent(precinctNum)}`)
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 404) setError(`Precinct ${precinctNum} not found in the database yet.`);
        else if (code === 401) setError('Not authenticated — log in to see live precinct data.');
        else setError(err?.message ?? 'Unknown error loading precinct data.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [precinctNum]);

  // Navigation chrome (subregion / LD / map file) comes from the tenant's turf
  // catalog. `regionForPrecinct` owns the number-vs-string coercion that used
  // to be a two-armed `includes()` here; null is the honest answer both while
  // the catalog loads and for a tenant with no turfs, and every read below is
  // already optional-chained.
  const srData = useMemo(
    () => regionForPrecinct(regions, precinctNum),
    [regions, precinctNum],
  );
  const sr = srData?.key;
  const ld = srData?.ld;

  // ---------- loading state ----------
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-border-base bg-bg-raised/60 p-12 text-center">
          <div className="inline-block h-3 w-3 rounded-full bg-emerald-500 animate-pulse mr-2" />
          <span className="text-fg-secondary">
            Loading precinct {precinctNum} from Postgres...
          </span>
        </div>
      </div>
    );
  }

  // ---------- error state ----------
  if (error || !data) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-status-danger bg-status-danger-bg/40 p-6 text-status-danger">
          <div className="font-semibold mb-1">Couldn't load precinct {precinctNum}</div>
          <div className="text-sm text-status-danger/80">{error}</div>
        </div>
      </div>
    );
  }

  // ---------- success state: build chart data ----------
  const tierData = toChartData(data.by_tier, TIER_DISPLAY_ORDER, TIER_LABELS);
  const partyData = toChartData(data.by_party, undefined, PARTY_LABELS).sort(
    (a, b) => b.value - a.value,
  );
  const ageData = data.by_age_bucket.map((b) => ({ name: b.bucket, value: b.count }));
  const cityEntries = Object.entries(data.by_city);

  // Turnout history chart: newest-first → oldest-first for the X axis,
  // labeled "YYYY-MM-DD · Type" so it's unambiguous.
  const turnoutData = [...data.election_history]
    .sort((a, b) => a.election_date.localeCompare(b.election_date))
    .map((e) => ({
      name: `${e.election_date.slice(0, 10)}`,
      fullLabel: `${e.election_date} ${e.election_type}`,
      voters: e.voter_count,
      type: e.election_type,
    }));

  const activePct = data.total_voters
    ? (data.active_voters * 100) / data.total_voters
    : 0;

  return (
    <div className="space-y-6">
      {/* Breadcrumb (navigation chrome only — not live data) */}
      <Breadcrumb
        items={[
          { label: 'County Map', action: 'map' },
          ld && LD_NAMES[ld] ? { label: LD_NAMES[ld], action: () => onDrill('ld', ld) } : null,
          sr && srData
            ? {
                label: `${sr} ${srData.name}`,
                action: () => onDrill('subregion', sr),
                mapFile: srData.mapFile,
              }
            : null,
          { label: `P${precinctNum} ${data.precinct_name}`, action: null },
        ].filter(Boolean)}
        onNavigate={onDrill}
      />

      <OnboardingModal
        viewKey="precinctview"
        title={`Precinct ${precinctNum} — ${data.precinct_name}`}
        intro="Single-precinct deep dive. Every number on this page is computed live from the voter roll, joined with each voter's election history. Use this view to understand who lives here, how they engage, and how to talk to them at the door."
        termIds={[
          'precinct',
          'tier_overview',
          'avg_score',
          'super_voter_pct',
          'roster_turnout',
          'age_component',
        ]}
        workflows={[
          'Glance at the tier mix to know what mix of doors a canvasser will hit.',
          'Check the past-election turnout chart for cyclical voters worth re-engaging.',
          'Open the precinct map PDF and walking route before sending a shift.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => setForceOnboard(false)}
      />

      {/* Header strip */}
      <ViewHeader
        title={`Precinct ${precinctNum} — ${data.precinct_name}`}
        tagline={
          <>
            {sr && srData ? `${sr} ${srData.name} • ` : ''}
            {ld && LD_NAMES[ld] ? `${LD_NAMES[ld]} • ` : ''}
            {cityEntries.map(([c]) => c).join(', ')}
          </>
        }
        inline={
          <>
            <InfoTip termId="precinct" />
          </>
        }
        actions={
          <>
            <OnboardingHelpButton onClick={() => setForceOnboard(true)} />
            {srData?.mapFile && (
              <a
                href={`/maps/${srData.mapFile}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded-lg text-sm font-bold"
              >
                Precinct Map
              </a>
            )}
            {sr && GMAPS[sr] && GMAPS[sr].shifts?.[0]?.url && (
              <a
                href={GMAPS[sr].shifts[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-status-info-bg hover:bg-status-info-bg/80 text-white px-4 py-2 rounded-lg text-sm font-bold"
              >
                Walking Route
              </a>
            )}
            {canExportWalkList && (
              <>
                <button
                  onClick={handlePrintWalkList}
                  disabled={downloadingHtml}
                  className="bg-status-warning-bg hover:bg-status-warning-bg/80 disabled:bg-bg-elevated text-white px-4 py-2 rounded-lg text-sm font-bold transition"
                >
                  {downloadingHtml ? 'Opening...' : 'Print Walk List'}
                </button>
                <button
                  onClick={handleDownloadWalkListCsv}
                  disabled={downloadingCsv}
                  className="bg-status-success-bg hover:bg-status-success-bg/80 disabled:bg-bg-elevated text-white px-4 py-2 rounded-lg text-sm font-bold transition"
                >
                  {downloadingCsv ? 'Downloading...' : 'Download CSV'}
                </button>
              </>
            )}
          </>
        }
      />

      {/* Walk list filter */}
      {downloadError && (
        <div className="bg-status-danger-bg/20 border border-status-danger/30 rounded-lg p-3 text-sm text-status-danger">
          {downloadError}
        </div>
      )}
      <div className="bg-bg-raised rounded-lg border border-border-base p-3 flex gap-3 items-end flex-wrap">
        <div>
          <label className="block text-fg-muted text-xs font-semibold uppercase mb-1">
            Filter by Tier (optional)
          </label>
          <select
            value={selectedTier ?? ''}
            onChange={(e) => setSelectedTier(e.target.value || undefined)}
            className="bg-bg-base border border-border-subtle rounded px-2 py-1 text-fg-primary text-sm"
          >
            <option value="">All Tiers</option>
            <option value="anchor">Anchor</option>
            <option value="warm">Warm</option>
            <option value="cool">Cold</option>
          </select>
        </div>
        <p className="text-fg-muted text-xs">
          Applies to both CSV download and print preview above.
        </p>
      </div>

      <TierLegend />
      <VisualKey />

      {/* Headline stats row (all live) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Total Voters" value={data.total_voters.toLocaleString()} termId="active_voter" />
        <Stat label="Active %" value={activePct.toFixed(1)} suffix="%" termId="active_voter">
          <BarMeter value={activePct} tierColor={false} />
          {countyBl && (
            <DeltaChip
              value={activePct}
              baseline={countyBl.activePct}
              label="vs county"
              format="pct"
              compact
            />
          )}
        </Stat>
        <Stat label="Avg Score" value={data.avg_score.toFixed(1)} termId="avg_score">
          <div className="flex items-center gap-1 flex-wrap">
            <TierPill score={data.avg_score} size="sm" />
            {countyBl && (
              <DeltaChip
                value={data.avg_score}
                baseline={countyBl.avgScore}
                label="vs county"
                format="num"
                compact
              />
            )}
          </div>
          <BarMeter
            value={data.avg_score}
            baseline={countyBl?.avgScore ?? null}
          />
        </Stat>
        <Stat label="Super Voter %" value={data.super_voter_pct.toFixed(2)} suffix="%" termId="super_voter_pct">
          {countyBl && (
            <DeltaChip
              value={data.super_voter_pct}
              baseline={countyBl.superVoterPct}
              label="vs county"
              format="pct"
              compact
            />
          )}
          <BarMeter value={data.super_voter_pct} tierColor={false} />
        </Stat>
        <Stat label="Primary Voter %" value={data.primary_voter_pct.toFixed(1)} suffix="%" termId="primary_voter">
          {countyBl && (
            <DeltaChip
              value={data.primary_voter_pct}
              baseline={countyBl.primaryVoterPct}
              label="vs county"
              format="pct"
              compact
            />
          )}
          <BarMeter value={data.primary_voter_pct} tierColor={false} />
        </Stat>
        <Stat
          label="Past Elections"
          value={data.election_history.length.toLocaleString()}
          termId="roster_turnout"
        />
      </div>

      {/* Turnout History chart — the new data unlocked by voter_elections */}
      <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-bold text-fg-primary flex items-center gap-2">
            Past Election Turnout <InfoTip termId="roster_turnout" />
          </h2>
          <div className="text-xs text-fg-muted">
            Ballots cast per election by voters currently registered in this precinct
            (most recent {Math.min(turnoutData.length, 30)} elections)
          </div>
        </div>
        {turnoutData.length === 0 ? (
          <div className="text-fg-muted text-sm py-8 text-center">
            No election history recorded for voters in this precinct.
          </div>
        ) : (() => {
          const firstT = turnoutData[0];
          const lastT = turnoutData[turnoutData.length - 1];
          const delta = lastT.voters - firstT.voters;
          const dir = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
          const pct = firstT.voters
            ? ((delta / firstT.voters) * 100).toFixed(1)
            : '0';
          const shortLabel = `Line chart: past-election turnout for precinct ${precinctNum} ${data.precinct_name} across ${turnoutData.length} elections, ${dir} ${Math.abs(Number(pct))}% from ${firstT.voters.toLocaleString()} to ${lastT.voters.toLocaleString()} voters.`;
          const longNarrative = `Past election turnout for precinct ${precinctNum} ${data.precinct_name}: ${turnoutData
            .map((t) => `${t.fullLabel} ${t.voters.toLocaleString()} voters`)
            .join('; ')}. Overall ${dir} ${Math.abs(Number(pct))}% from first to last recorded election.`;
          return (
            <figure aria-label={shortLabel} className="m-0">
              <figcaption className="sr-only">{longNarrative}</figcaption>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={turnoutData} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: 'var(--chart-axis)', fontSize: 10 }}
                    angle={-45}
                    height={60}
                    textAnchor="end"
                  />
                  <YAxis tick={{ fill: 'var(--chart-axis)' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgb(var(--bg-elevated))',
                      border: '1px solid rgb(var(--border-base))',
                      borderRadius: 6,
                    }}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel ?? ''}
                  />
                  <Line
                    type="monotone"
                    dataKey="voters"
                    stroke="var(--accent)"
                    strokeWidth={2.5}
                    dot={{ fill: 'var(--accent)', r: 4 }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={!reduceMotion}
                  />
                </LineChart>
              </ResponsiveContainer>
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                  Show data as table
                </summary>
                <table className="mt-2 w-full">
                  <thead>
                    <tr>
                      <th className="text-left py-1 px-2 text-fg-muted">Election</th>
                      <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                    </tr>
                  </thead>
                  <tbody>
                    {turnoutData.map((t) => (
                      <tr key={t.fullLabel} className="border-t border-border-base">
                        <td className="py-1 px-2 text-fg-secondary">{t.fullLabel}</td>
                        <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                          {t.voters.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </figure>
          );
        })()}
      </div>

      {/* Distributions row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            Engagement Tiers <InfoTip termId="tier_overview" />
          </h2>
          {(() => {
            const top = [...tierData].sort((a, b) => b.value - a.value)[0];
            const total = tierData.reduce((s, d) => s + d.value, 0);
            const topPct = total > 0 && top ? ((top.value / total) * 100).toFixed(1) : '0';
            const shortLabel = top
              ? `Bar chart: engagement tier distribution across ${tierData.length} tiers for precinct ${precinctNum}. ${top.name} dominates with ${top.value.toLocaleString()} voters (${topPct}% of the precinct).`
              : `Bar chart: engagement tiers for precinct ${precinctNum}. No data.`;
            const longNarrative = `Engagement tiers in precinct ${precinctNum} ${data.precinct_name}: ${tierData
              .map(
                (d) =>
                  `${d.name} ${d.value.toLocaleString()} voters (${
                    total > 0 ? ((d.value / total) * 100).toFixed(1) : '0'
                  }%)`,
              )
              .join('; ')}.`;
            return (
              <figure aria-label={shortLabel} className="m-0">
                <figcaption className="sr-only">{longNarrative}</figcaption>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={tierData}>
                    <XAxis
                      dataKey="name"
                      tick={{ fill: 'var(--chart-axis)', fontSize: 11 }}
                      angle={-25}
                      height={60}
                    />
                    <YAxis tick={{ fill: 'var(--chart-axis)' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'rgb(var(--bg-elevated))', border: '1px solid rgb(var(--border-base))' }}
                    />
                    <Bar dataKey="value" fill="var(--accent)" isAnimationActive={!reduceMotion} />
                  </BarChart>
                </ResponsiveContainer>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                    Show data as table
                  </summary>
                  <table className="mt-2 w-full">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-fg-muted">Tier</th>
                        <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tierData.map((d) => (
                        <tr key={d.name} className="border-t border-border-base">
                          <td className="py-1 px-2 text-fg-secondary">{d.name}</td>
                          <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                            {d.value.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </figure>
            );
          })()}
        </div>

        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            Party Registration <InfoTip termId="party_component" />
          </h2>
          {(() => {
            const top = partyData[0];
            const total = partyData.reduce((s, d) => s + d.value, 0);
            const topPct = total > 0 && top ? ((top.value / total) * 100).toFixed(1) : '0';
            const shortLabel = top
              ? `Bar chart: party registration across ${partyData.length} parties for precinct ${precinctNum}. ${top.name} leads with ${top.value.toLocaleString()} voters (${topPct}%).`
              : `Bar chart: party registration for precinct ${precinctNum}. No data.`;
            const longNarrative = `Party registration in precinct ${precinctNum} ${data.precinct_name}: ${partyData
              .map(
                (d) =>
                  `${d.name} ${d.value.toLocaleString()} voters (${
                    total > 0 ? ((d.value / total) * 100).toFixed(1) : '0'
                  }%)`,
              )
              .join('; ')}.`;
            return (
              <figure aria-label={shortLabel} className="m-0">
                <figcaption className="sr-only">{longNarrative}</figcaption>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={partyData}>
                    <XAxis
                      dataKey="name"
                      tick={{ fill: 'var(--chart-axis)', fontSize: 11 }}
                      angle={-15}
                      height={60}
                    />
                    <YAxis tick={{ fill: 'var(--chart-axis)' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'rgb(var(--bg-elevated))', border: '1px solid rgb(var(--border-base))' }}
                    />
                    <Bar dataKey="value" fill="var(--chart-series-2)" isAnimationActive={!reduceMotion} />
                  </BarChart>
                </ResponsiveContainer>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                    Show data as table
                  </summary>
                  <table className="mt-2 w-full">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-fg-muted">Party</th>
                        <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partyData.map((d) => (
                        <tr key={d.name} className="border-t border-border-base">
                          <td className="py-1 px-2 text-fg-secondary">{d.name}</td>
                          <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                            {d.value.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </figure>
            );
          })()}
          <div className="text-[11px] text-fg-muted mt-2">
            Missouri is an open-primary state with no party registration — most voters will
            be "No Party".
          </div>
        </div>

        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            Age Distribution <InfoTip termId="age_component" />
          </h2>
          {ageData.every((d) => d.name === 'Unknown') ? (
            <div className="text-fg-muted text-sm py-8 text-center">
              Birth dates not present in the current source file — age distribution
              will populate when a file with DOB data is imported.
            </div>
          ) : (() => {
            const top = [...ageData].sort((a, b) => b.value - a.value)[0];
            const total = ageData.reduce((s, d) => s + d.value, 0);
            const topPct = total > 0 && top ? ((top.value / total) * 100).toFixed(1) : '0';
            const shortLabel = top
              ? `Bar chart: age distribution across ${ageData.length} age buckets for precinct ${precinctNum}. Largest bucket ${top.name} with ${top.value.toLocaleString()} voters (${topPct}%).`
              : `Bar chart: age distribution for precinct ${precinctNum}. No data.`;
            const longNarrative = `Age distribution in precinct ${precinctNum} ${data.precinct_name}: ${ageData
              .map(
                (d) =>
                  `${d.name} ${d.value.toLocaleString()} voters (${
                    total > 0 ? ((d.value / total) * 100).toFixed(1) : '0'
                  }%)`,
              )
              .join('; ')}.`;
            return (
              <figure aria-label={shortLabel} className="m-0">
                <figcaption className="sr-only">{longNarrative}</figcaption>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={ageData}>
                    <XAxis dataKey="name" tick={{ fill: 'var(--chart-axis)' }} />
                    <YAxis tick={{ fill: 'var(--chart-axis)' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'rgb(var(--bg-elevated))', border: '1px solid rgb(var(--border-base))' }}
                    />
                    <Bar dataKey="value" fill="var(--chart-series-3)" isAnimationActive={!reduceMotion} />
                  </BarChart>
                </ResponsiveContainer>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                    Show data as table
                  </summary>
                  <table className="mt-2 w-full">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-fg-muted">Age bucket</th>
                        <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ageData.map((d) => (
                        <tr key={d.name} className="border-t border-border-base">
                          <td className="py-1 px-2 text-fg-secondary">{d.name}</td>
                          <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                            {d.value.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </figure>
            );
          })()}
        </div>
      </div>

      {/* Cities breakdown */}
      {cityEntries.length > 0 && (
        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            Cities
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {cityEntries.map(([city, count]) => (
              <div
                key={city}
                className="bg-bg-base rounded-lg p-4 border border-border-base"
              >
                <div className="text-xs text-fg-muted">{city}</div>
                <div className="text-2xl font-bold text-fg-primary tabular-nums">
                  {count.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Map view link */}
      {srData?.mapFile && (
        <div className="flex gap-4">
          <a
            href={`/maps/${srData.mapFile}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-accent hover:bg-accent-hover text-white font-bold py-3 px-4 rounded-lg transition text-center"
          >
            View Subregion Map
          </a>
        </div>
      )}
    </div>
  );
};
